import { FC } from 'react'
import * as yup from 'yup'
import { Formik, FormikValues } from 'formik'
import styles from './CreateAdmin.module.css'
import close from '../../assets/icons/close.svg'
import s from '../../styles/common/Form.module.css'
import CustomInput from '../UI/CustomInput/CustomInput'
import CustomButton from '../UI/CustomButton/CustomButton'
import CustomRadio from '../UI/CustomRadio/CustomRadio'
import usePasswordToggle from '../../hooks/usePasswordToggle'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import { getAdmins, postAdmin } from '../../redux/actions/adminsAction'
import { createRoleForRegister } from '../../utils/createRoleForRegister'
import p from '../UI/CustomInput/CustomInput.module.css'
import InputMask from 'react-input-mask'
import { adminsPerPage } from '../../constants/constants'
import { useToast } from '../../context/ToastContext'

export type CreateUserPropsType = {
  onClickHandler: () => void
}

const CreateAdmin: FC<CreateUserPropsType> = ({ onClickHandler }) => {
  const [PasswordInputType, ToggleIcon] = usePasswordToggle()
  const { token } = useAppSelector((state) => state.auth)
  const { showSuccess, showError } = useToast()

  const dispatch = useAppDispatch()

  const validationSchema = yup.object().shape({
    fullName: yup.string().required('Поле является обязательным'),
    phone: yup.string().required('Поле является обязательным'),
    email: yup
      .string()
      .email('К сожалению, почта введена неверно, попробуйте еще раз')
      .required('Поле является обязательным'),
    password: yup
      .string()
      .required('Поле является обязательным')
      .min(8, 'Пароль должен' + ' содержать' + ' минимум 8' + ' символов'),
    role: yup.string().required('Поле является обязательным'),
  })

  const handleSubmitHandler = async (values: FormikValues) => {
    const mobileWithoutSymbols = `+${values.phone.replace(/[\D]+/g, '')}`
    const resultAction = await dispatch(
      postAdmin({
        token,
        role: createRoleForRegister(values.role),
        phone: mobileWithoutSymbols,
        email: values.email,
        password: values.password,
        fullName: values.fullName,
      }),
    )

    if (postAdmin.rejected.match(resultAction)) {
      showError('Произошла ошибка при создании админа')
    } else {
      onClickHandler()
      showSuccess('Админ успешно создан')
      await dispatch(getAdmins({ token, page: 1, itemsPerPage: adminsPerPage }))
    }
  }

  return (
    <div className={styles.registrationModalOverlay}>
      <div className={styles.registrationModal}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Создание администратора</h1>
          <img onClick={onClickHandler} className={styles.close} src={close} alt='close' />
        </div>
        <div className={styles.form}>
          <Formik
            initialValues={{
              fullName: '',
              phone: '',
              email: '',
              password: '',
              role: '',
            }}
            validateOnBlur
            onSubmit={(values) => handleSubmitHandler(values)}
            validationSchema={validationSchema}
          >
            {(props) => {
              const { values, touched, errors, isValid, handleChange, handleBlur, handleSubmit } =
                props

              return (
                <div className={styles.formContainer}>
                  <div className={styles.inputGroup}>
                    <div className={styles.inputContainer}>
                      <h4 className={styles.subtitle}>Роль</h4>
                      <div className={styles.inputContainerWithError}>
                        <CustomRadio
                          options={['Суперадминистратор', 'Администратор']}
                          name='role'
                          onChange={handleChange}
                          value={values.role}
                        />
                      </div>
                    </div>
                    <div className={styles.inputContainer}>
                      <h4 className={styles.subtitle}>ФИО</h4>
                      <div className={styles.inputContainerWithError}>
                        <CustomInput
                          autoComplete='off'
                          type='text'
                          placeholder='ФИО'
                          name='fullName'
                          required
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.fullName}
                          className={
                            errors.fullName && touched.fullName
                              ? `${styles.inputError} ${styles.input}`
                              : styles.input
                          }
                        />
                        {touched.fullName && errors.fullName && (
                          <p className={s.errorValidation}>{errors.fullName}</p>
                        )}
                      </div>
                    </div>
                    <div className={styles.inputContainer}>
                      <h4 className={styles.subtitle}>Номер телефона</h4>
                      <div className={styles.inputContainerWithError}>
                        <InputMask
                          mask='+7 (999) 999 9999'
                          type='phone'
                          placeholder='Номер телефона'
                          name='phone'
                          required
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.phone}
                          className={
                            errors.phone && touched.phone
                              ? `${s.inputError} ${p.input}`
                              : `${s.input} ${p.input}`
                          }
                        />
                        {touched.phone && errors.phone && (
                          <p className={s.errorValidation}>{errors.phone}</p>
                        )}
                      </div>
                    </div>
                    <div className={styles.inputContainer}>
                      <h4 className={styles.subtitle}>Электронная почта/Логин</h4>
                      <div className={styles.inputContainerWithError}>
                        <CustomInput
                          autoComplete='new-email'
                          type='email'
                          placeholder='Email'
                          name='email'
                          required
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.email}
                          className={styles.input}
                        />
                        {touched.email && errors.email && (
                          <p className={s.errorValidation}>{errors.email}</p>
                        )}
                      </div>
                    </div>
                    <div className={styles.inputContainer}>
                      <h4 className={styles.subtitle}>Пароль</h4>
                      <div className={styles.inputContainerWithError}>
                        <div className={styles.passInput}>
                          <CustomInput
                            autoComplete='new-password'
                            type={PasswordInputType}
                            placeholder='Пароль'
                            name='password'
                            required
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.password}
                            className={styles.input}
                          />
                          <div className={styles.eyeIcon}>{ToggleIcon}</div>
                        </div>
                        {touched.password && errors.password && (
                          <p className={s.errorValidation}>{errors.password}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.footerContainer}>
                    <CustomButton
                      className={styles.submitBtn}
                      title='Создать'
                      disabled={!isValid}
                      onClick={() => handleSubmit()}
                      type='submit'
                    />
                  </div>
                </div>
              )
            }}
          </Formik>
        </div>
      </div>
    </div>
  )
}

export default CreateAdmin
